// [S-216] EditTagsModal — retroactively retag any existing template.
//
// Works in every lifecycle state (draft / certified / re_certified /
// under_review / deprecated) because tags are metadata, not part of
// the FSM. The "Auto-tag from content" button calls the same endpoint
// TemplateAuthor uses on upload, fills the form with the suggestion,
// and renders a rationale inline so the user can sanity-check.
//
// The Save button is disabled until at least one tag actually differs
// from the persisted template — keeps the audit log signal-to-noise
// high (no "user opened modal and clicked save" no-op events).
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogClose,
  Select,
} from "../index";
import {
  useUpdateTemplateTags,
  type CertifiedTemplateRow,
} from "../../data/templatesQuery";
import {
  useAutoTagL1Template,
  type TemplateTagSuggestion,
} from "../../data/l1TemplatesQuery";
import { useAutoTagL2Template } from "../../data/l2TemplatesQuery";
import { getApiErrorMessage } from "../../data/api";
import { TALENT_TIERS } from "../../lib/talentTier";

const DEAL_TYPES: ReadonlyArray<{
  value: CertifiedTemplateRow["dealType"];
  label: string;
}> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{
  value: CertifiedTemplateRow["guild"];
  label: string;
}> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS = TALENT_TIERS;

// [S-633] Agreement form. "none" is the UI sentinel for untagged (→ null on the
// wire); "long_form" is what the deal-memo → long-form promotion selects on.
const DOCUMENT_FORM_NONE = "none";
const DOCUMENT_FORMS: ReadonlyArray<{ value: string; label: string }> = [
  { value: DOCUMENT_FORM_NONE, label: "Untagged" },
  { value: "deal_memo", label: "Deal memo (short form)" },
  { value: "long_form", label: "Long form" },
];

const ANCHOR_CBA_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "SAG-AFTRA 2026 Theatrical", label: "SAG-AFTRA 2026 Theatrical" },
  { value: "SAG-AFTRA 2026 TV", label: "SAG-AFTRA 2026 TV" },
  {
    value: "SAG-AFTRA 2026 Theatrical/Streaming",
    label: "SAG-AFTRA 2026 Theatrical/Streaming",
  },
  { value: "DGA Basic Agreement 2023", label: "DGA Basic Agreement 2023" },
  { value: "WGA MBA 2026", label: "WGA MBA 2026" },
  { value: "IATSE Basic Agreement 2021", label: "IATSE Basic Agreement 2021" },
];

export type EditTagsModalProps = {
  /** Template being retagged; null hides the dialog. */
  template: CertifiedTemplateRow | null;
  onClose: () => void;
};

export function EditTagsModal({
  template,
  onClose,
}: EditTagsModalProps): JSX.Element {
  const open = template !== null;

  // Form state — initialised from the template each time the dialog
  // opens with a new template id.
  const [title, setTitle] = useState<string>("");
  const [dealType, setDealType] = useState<
    CertifiedTemplateRow["dealType"] | undefined
  >(undefined);
  const [guild, setGuild] = useState<CertifiedTemplateRow["guild"] | undefined>(
    undefined
  );
  const [tier, setTier] = useState<CertifiedTemplateRow["tier"] | undefined>(
    undefined
  );
  const [anchorCBA, setAnchorCBA] = useState<string | undefined>(undefined);
  // [S-633] Agreement form (deal_memo | long_form); DOCUMENT_FORM_NONE = untagged.
  const [documentForm, setDocumentForm] = useState<string>(DOCUMENT_FORM_NONE);
  const [rationale, setRationale] = useState<
    TemplateTagSuggestion["rationale"] | null
  >(null);
  const [confidence, setConfidence] = useState<number | null>(null);

  useEffect(() => {
    if (template === null) return;
    setTitle(template.title ?? "");
    setDealType(template.dealType);
    setGuild(template.guild);
    setTier(template.tier);
    setAnchorCBA(template.anchorCBA ?? undefined);
    setDocumentForm(template.documentForm ?? DOCUMENT_FORM_NONE);
    setRationale(null);
    setConfidence(null);
  }, [template]);

  // Layer-aware auto-tag hook. tenantId === null → L1; non-null → L2.
  // We instantiate both and pick at request time.
  const autoTagL1 = useAutoTagL1Template();
  const autoTagL2 = useAutoTagL2Template(template?.tenantId ?? "");
  const autoTag =
    template?.tenantId === null ? autoTagL1 : autoTagL2;

  const update = useUpdateTemplateTags();

  const handleAutoTag = (): void => {
    if (template === null) return;
    autoTag.mutate(
      { content: template.content },
      {
        onSuccess: (s) => {
          setTitle(s.title);
          setDealType(s.dealType);
          setGuild(s.guild);
          setTier(s.tier);
          if (s.anchorCBA !== null) setAnchorCBA(s.anchorCBA);
          setRationale(s.rationale);
          setConfidence(s.confidence);
        },
      }
    );
  };

  // Diff against the persisted template — Save is disabled when no
  // field has actually changed, so the audit log only captures real
  // retags.
  const dirty = useMemo(() => {
    if (template === null) return false;
    const currentTitle = template.title ?? "";
    if (title.trim() !== currentTitle.trim()) return true;
    if (dealType !== template.dealType) return true;
    if (guild !== template.guild) return true;
    if (tier !== template.tier) return true;
    const currentCBA = template.anchorCBA ?? null;
    const formCBA =
      anchorCBA === undefined || anchorCBA.length === 0 ? null : anchorCBA;
    if (currentCBA !== formCBA) return true;
    const currentForm = template.documentForm ?? null;
    const formForm = documentForm === DOCUMENT_FORM_NONE ? null : documentForm;
    if (currentForm !== formForm) return true;
    return false;
  }, [template, title, dealType, guild, tier, anchorCBA, documentForm]);

  const handleSave = (): void => {
    if (template === null || !dirty) return;
    // Send only fields that actually changed — keeps the audit payload
    // minimal and the server's refine() check happy.
    const patch: Record<string, unknown> = {};
    const currentTitle = template.title ?? "";
    const formTitle = title.trim();
    if (formTitle !== currentTitle.trim()) {
      patch["title"] = formTitle.length === 0 ? null : formTitle;
    }
    if (dealType !== template.dealType) patch["dealType"] = dealType;
    if (guild !== template.guild) patch["guild"] = guild;
    if (tier !== template.tier) patch["tier"] = tier;
    const currentCBA = template.anchorCBA ?? null;
    const formCBA =
      anchorCBA === undefined || anchorCBA.length === 0 ? null : anchorCBA;
    if (currentCBA !== formCBA) patch["anchorCBA"] = formCBA;
    const currentForm = template.documentForm ?? null;
    const formForm = documentForm === DOCUMENT_FORM_NONE ? null : documentForm;
    if (currentForm !== formForm) patch["documentForm"] = formForm;

    update.mutate(
      {
        templateId: template.id,
        tenantId: template.tenantId,
        ...patch,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const autoTagError = getApiErrorMessage(autoTag.error) ?? undefined;
  const saveError = getApiErrorMessage(update.error) ?? undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Edit tags"
      description="Update this template's taxonomy. Lifecycle state and effective dates aren't affected."
    >
      <div className="td__retag" data-testid="td-retag-modal">
        <div className="td__retag__head">
          <div className="td__retag__id">
            <code>{template?.id ?? ""}</code>
            <span className="td__retag__state">
              {template?.lifecycleState ?? ""}
            </span>
          </div>
          <Button
            variant="secondary"
            onClick={handleAutoTag}
            disabled={autoTag.isPending || template === null}
            data-testid="td-retag-autotag"
          >
            {autoTag.isPending ? "Tagging…" : "Auto-tag from content"}
          </Button>
        </div>

        {confidence !== null && (
          <div className="td__retag__conf" data-testid="td-retag-confidence">
            Auto-tag confidence: <b>{Math.round(confidence * 100)}%</b> —
            review each field and override anything that looks wrong.
          </div>
        )}
        {autoTagError !== undefined && (
          <div className="tpl-author__warn" role="alert">
            Auto-tag failed — {autoTagError}
          </div>
        )}

        <RetagRow label="Title" rationale={rationale?.title}>
          <input
            type="text"
            className="tpl-author__title-input"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setRationale((r) => (r === null ? r : { ...r, title: "" }));
            }}
            placeholder="e.g. Theatrical Actor Deal Memo (SAG-AFTRA)"
            data-testid="td-retag-title-input"
          />
        </RetagRow>

        <RetagRow label="Deal type" rationale={rationale?.dealType}>
          <Select
            label="Deal type"
            value={dealType}
            options={DEAL_TYPES}
            onValueChange={(v) => {
              if (v !== undefined) {
                setDealType(v);
                setRationale((r) => (r === null ? r : { ...r, dealType: "" }));
              }
            }}
            clearable={false}
          />
        </RetagRow>
        <RetagRow label="Guild" rationale={rationale?.guild}>
          <Select
            label="Guild"
            value={guild}
            options={GUILDS}
            onValueChange={(v) => {
              if (v !== undefined) {
                setGuild(v);
                setRationale((r) => (r === null ? r : { ...r, guild: "" }));
              }
            }}
            clearable={false}
          />
        </RetagRow>
        <RetagRow label="Tier" rationale={rationale?.tier}>
          <Select
            label="Tier"
            value={tier}
            options={TIERS}
            onValueChange={(v) => {
              if (v !== undefined) {
                setTier(v);
                setRationale((r) => (r === null ? r : { ...r, tier: "" }));
              }
            }}
            clearable={false}
          />
        </RetagRow>
        <RetagRow label="Anchor CBA" rationale={rationale?.anchorCBA}>
          <Select<string>
            label="Anchor CBA"
            value={anchorCBA}
            // Keep an off-list / auto-tagged CBA visible + selectable
            // instead of letting the picker render blank (see TemplateAuthor).
            options={
              anchorCBA !== undefined &&
              !ANCHOR_CBA_OPTIONS.some((o) => o.value === anchorCBA)
                ? [
                    ...ANCHOR_CBA_OPTIONS,
                    { value: anchorCBA, label: `${anchorCBA} (auto-tagged)` },
                  ]
                : ANCHOR_CBA_OPTIONS
            }
            onValueChange={(v) => {
              if (v !== undefined) {
                setAnchorCBA(v);
                setRationale((r) =>
                  r === null ? r : { ...r, anchorCBA: "" }
                );
              }
            }}
            clearable={false}
            placeholder="Pick an anchor CBA…"
          />
        </RetagRow>
        {/* [S-633] Agreement form — tag a certified template as the long form so
            a closed deal memo of the same deal type can be promoted into it. */}
        <RetagRow label="Agreement form" rationale={undefined}>
          <Select<string>
            label="Agreement form"
            value={documentForm}
            options={DOCUMENT_FORMS}
            onValueChange={(v) => {
              if (v !== undefined) setDocumentForm(v);
            }}
            clearable={false}
          />
        </RetagRow>

        {saveError !== undefined && (
          <div className="td__attest__err" role="alert">
            Save failed — {saveError}
          </div>
        )}

        <div className="td__retag__actions">
          <DialogClose asChild>
            <Button variant="ghost" data-testid="td-retag-cancel">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={update.isPending || !dirty}
            data-testid="td-retag-save"
            title={
              dirty
                ? undefined
                : "Change at least one tag before saving."
            }
          >
            {update.isPending ? "Saving…" : "Save tags"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function RetagRow({
  label,
  rationale,
  children,
}: {
  label: string;
  rationale: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="td__retag__row">
      <label>{label}</label>
      {children}
      {rationale !== undefined && rationale.length > 0 && (
        <div
          className="tpl-author__meta__rationale"
          data-testid={`td-retag-rationale-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <span aria-hidden="true">↳</span> {rationale}
        </div>
      )}
    </div>
  );
}
